/**
 * Canonical display order for academic grade labels. Used everywhere a
 * grade list is rendered without a backend `order_number` — for example,
 * the Teaching Assignments card on the Staff Profile flattens grades
 * inside a subject and cannot rely on the SQL ORDER BY on grades.
 *
 * The map key must match the exact grade label stored in the DB. A
 * couple of common written variants are recognised too, so a slightly-
 * different label (e.g. "Grade 3", "PreK") doesn't fall off the end of
 * the sort into "unknown".
 *
 * Any label not in the map sorts last, in alphabetical order.
 */
const CANONICAL: Record<string, number> = {
  "Pre-K": 1,
  JK: 2,
  SK: 3,
  "1": 4,
  "2": 5,
  "3": 6,
  "4": 7,
  "5": 8,
  "6": 9,
  "7": 10,
  "8": 11,
  "9": 12,
  "10": 13,
  "11": 14,
  "12": 15,
  Adult: 16,
};

const ALIASES: Record<string, string> = {
  PreK: "Pre-K",
  "Pre K": "Pre-K",
  "PRE-K": "Pre-K",
  ADULT: "Adult",
  adult: "Adult",
  jk: "JK",
  sk: "SK",
};
for (let i = 1; i <= 12; i++) {
  ALIASES[`Grade ${i}`] = String(i);
  ALIASES[`grade ${i}`] = String(i);
  ALIASES[`GRADE ${i}`] = String(i);
}

export function gradeOrderKey(label: string | number | null | undefined): number {
  const raw = label == null ? "" : String(label).trim();
  if (raw === "") return Number.MAX_SAFE_INTEGER;
  if (raw in CANONICAL) return CANONICAL[raw];
  const alias = ALIASES[raw];
  if (alias && alias in CANONICAL) return CANONICAL[alias];
  return Number.MAX_SAFE_INTEGER;
}

/** Comparator for Array.sort() on { name } or { grade } shapes. */
export function compareGradeLabels(a: string, b: string): number {
  const ka = gradeOrderKey(a);
  const kb = gradeOrderKey(b);
  if (ka !== kb) return ka - kb;
  return a.localeCompare(b);
}
